/**
 * The on-site client's address as one line: street, then postal code and
 * city (« Rue des Chasseurs 5, 7100 La Louvière »). Clients often type the
 * whole address in the street field: the locality is not repeated then.
 */
export function fullAddress(client) {
  if (!client) return '';
  const street = (client.address || '').trim();
  const city = (client.city || '').trim();
  const locality = [client.postal_code, client.city]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');
  if (!locality) return street;
  const lower = street.toLowerCase();
  if (lower.includes(locality.toLowerCase()) || (city && lower.includes(city.toLowerCase()))) return street;
  return [street, locality].filter(Boolean).join(', ');
}

/**
 * The intervention address to show after the on-site client changes.
 *
 * By default it is the new client's address — but only while the field is
 * empty or still holds the previous client's address: a place typed by hand
 * (the flat downstairs, a second site) is never overwritten.
 */
export function addressAfterClientChange(current, previousClient, nextClient) {
  const now = (current || '').trim();
  const previousDefault = fullAddress(previousClient);
  if (!now || (previousDefault && now === previousDefault)) return fullAddress(nextClient);
  return current;
}

/**
 * The « numéro de contact de l'expertise » after the on-site client changes:
 * the new client's phone while the field is empty or still holds the previous
 * client's — a number typed by hand (the tenant, the caretaker) is kept.
 */
export function phoneAfterClientChange(current, previousClient, nextClient) {
  const now = (current || '').trim();
  const previousDefault = (previousClient?.phone || '').trim();
  if (!now || (previousDefault && now === previousDefault)) return (nextClient?.phone || '').trim();
  return current;
}

// Where an appointment / a report keeps its intervention fields.
export const APPOINTMENT_INTERVENTION = {
  name: 'contact_name', address: 'address', phone: 'contact_phone', email: 'contact_email',
};
export const REPORT_INTERVENTION = {
  name: 'client_name', address: 'location', phone: 'contact_phone', email: 'contact_email',
};

const clientValues = (client) => ({
  name: (client?.name || '').trim(),
  address: fullAddress(client),
  phone: (client?.phone || '').trim(),
  email: (client?.email || client?.billing_email || '').trim(),
});

/**
 * The intervention fields after a private person is chosen as the billing
 * client (they pay for their own home): each field takes their value while
 * it is empty or still holds the previous client's — anything typed by hand
 * (a tenant, another address) is kept. Returns only the fields to change.
 */
export function interventionFrom(current, previousClient, nextClient, keys = APPOINTMENT_INTERVENTION) {
  const before = clientValues(previousClient);
  const after = clientValues(nextClient);
  const out = {};
  Object.entries(keys).forEach(([what, field]) => {
    const now = (current[field] || '').trim();
    if ((!now || (before[what] && now === before[what])) && after[what] && after[what] !== now) {
      out[field] = after[what];
    }
  });
  return out;
}
